import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import API_BASE_URL from '../../../../../Constants';
import DeviceInspections from './DeviceInspections';

const ViewDeviceModal = ({
  open,
  onClose,

  // The entire devices array
  devices = [],
  currentIndex = 0,
  setCurrentIndex,

  // Fallback buildingSite & agencyId if the device doesn't have them
  buildingSite = '',
  agencyId = '',

  // Optional for updating array or re‐fetching
  setDevices,
  refreshDevices,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // The device the user is currently viewing
  const device = devices[currentIndex] || null;

  // Basic fields
  const [deviceId, setDeviceId] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [deviceSubType, setDeviceSubType] = useState('');
  const [localBuildingSite, setLocalBuildingSite] = useState('');
  const [localAgencyId, setLocalAgencyId] = useState('');

  // Additional checkboxes
  const [allowedToUpdateOther, setAllowedToUpdateOther] = useState(false);
  const [updateTestsAssigned, setUpdateTestsAssigned] = useState(false);
  const [updateDates, setUpdateDates] = useState(false);
  const [updatePerformFields, setUpdatePerformFields] = useState(false);
  const [updateDollarAmounts, setUpdateDollarAmounts] = useState(false);
  const [updateWitnessFields, setUpdateWitnessFields] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});

  // For device's inspection data
  const [inspectionData, setInspectionData] = useState([]);
  const [editedInspections, setEditedInspections] = useState([]);

  // -----------------------------
  // 1) On open => fill fields
  // -----------------------------
  useEffect(() => {
    if (!open || !device) return;

    // Fill from device, fallback to parent's buildingSite / agencyId
    setDeviceId(String(device.deviceId || ''));
    setDeviceType(device.deviceType || '');
    setDeviceSubType(device.deviceSubclass || '');
    setLocalBuildingSite(device.buildingSite || buildingSite);
    setLocalAgencyId(device.agencyId || agencyId);

    // Optionally fetch more detail
    fetchDevice(device.deviceId);
    fetchDeviceInspections(device.deviceId);
  }, [open, device, buildingSite, agencyId]);

  const fetchDevice = async (devId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/setup/buildings/get-device/${devId}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error(`Device fetch failed, status: ${res.status}`);
      }
      const data = await res.json();
      // If the API returns buildingSite or agencyId, overwrite:
      if (data.buildingSite && data.buildingSite.trim() !== '') {
        setLocalBuildingSite(data.buildingSite);
      }
      if (data.agencyId) {
        setLocalAgencyId(data.agencyId);
      }
    } catch (err) {
      console.error('Error fetching device data:', err);
    }
  };

  const fetchDeviceInspections = async (devId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/inspections/device/${devId}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error(`Inspection fetch failed, status: ${res.status}`);
      }
      const arr = await res.json();
      setInspectionData(arr);
      setEditedInspections(arr);
    } catch (err) {
      console.error('Error fetching device inspections:', err);
    }
  };

  // -----------------------------
  // 2) Expand / close
  // -----------------------------
  const toggleExpand = () => setIsExpanded(!isExpanded);

  const handleClose = () => {
    if (!isEditMode) {
      onClose();
    }
  };

  // -----------------------------
  // 3) Navigation (Prev/Next)
  // -----------------------------
  const totalDevices = devices.length;
  const handlePrevDevice = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };
  const handleNextDevice = () => {
    if (currentIndex < totalDevices - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  // -----------------------------
  // 4) Edit/Save
  // -----------------------------
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const validateFields = () => {
    const errors = {};
    if (!deviceId || deviceId.trim() === '') {
      errors.deviceId = 'Device ID is required.';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateFields()) {
      alert('Please fix errors before saving.');
      return;
    }
    const payload = {
      deviceId,
      deviceType,
      deviceSubType,
      buildingSite: localBuildingSite,
      agencyId: localAgencyId,

      allowedToUpdateOther,
      updateTestsAssigned,
      updateDates,
      updatePerformFields,
      updateDollarAmounts,
      updateWitnessFields,

      updatedInspections: editedInspections,
    };

    try {
      console.log('Saving device changes =>', payload);

      const res = await fetch(`${API_BASE_URL}/api/devices/${deviceId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`Failed to update device. Status: ${res.status}`);
      }
      alert('Device updated successfully!');
      setIsEditMode(false);

      if (refreshDevices) {
        refreshDevices();
      }
    } catch (err) {
      console.error('Error saving device changes:', err);
      alert('Failed to save device changes.');
    }
  };

  // As user types, store changes in local states
  const handleDeviceFieldChange = (fieldName, newValue) => {
    switch (fieldName) {
      case 'deviceId':
        setDeviceId(newValue);
        break;
      case 'deviceType':
        setDeviceType(newValue);
        break;
      case 'deviceSubType':
        setDeviceSubType(newValue);
        break;
      case 'buildingSite':
        setLocalBuildingSite(newValue);
        break;
      case 'agencyId':
        setLocalAgencyId(newValue);
        break;
      default:
        break;
    }

    // If we want to apply to entire array
    if (allowedToUpdateOther) {
      // Example: if user changed deviceType => update all deviceTypes
      if (updateTestsAssigned && fieldName === 'deviceType' && setDevices) {
        setDevices((prev) =>
          prev.map((d) => ({ ...d, deviceType: newValue }))
        );
      }
    }
  };

  // Sub‐component => handle inspection data changes
  const handleInspectionsChange = (updatedArray) => {
    setEditedInspections(updatedArray);
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isExpanded ? '90%' : '800px',
          maxHeight: '90vh',
          minHeight: '70vh',
        },
      }}
      fullWidth
      style={{ zIndex: 1400 }}
    >
      {/* HEADER */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4 border-b border-gray-300">
          {/* Left side */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold">
              {isEditMode
                ? `Edit Device: ${deviceId || ''}`
                : `View Device: ${deviceId || ''}`}
            </h2>
          </div>

          {/* Right side: Prev / Next / Expand */}
          <div className="flex items-center space-x-2">
            {totalDevices > 1 && (
              <>
                <button
                  onClick={handlePrevDevice}
                  disabled={currentIndex <= 0}
                  className="p-2 rounded-full hover:bg-gray-200 transition"
                  title="Previous Device"
                >
                  <ArrowBackIcon />
                </button>
                <button
                  onClick={handleNextDevice}
                  disabled={currentIndex >= totalDevices - 1}
                  className="p-2 rounded-full hover:bg-gray-200 transition"
                  title="Next Device"
                >
                  <ArrowForwardIcon />
                </button>
              </>
            )}
            <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>
        </div>

        {/* EDIT / SAVE Button Row */}
        <div className="p-4 border-b border-gray-300 flex space-x-4">
          {isEditMode ? (
            <>
              <button
                onClick={toggleEditMode}
                className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Save
              </button>
            </>
          ) : (
            <button
              onClick={toggleEditMode}
              className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* BODY */}
      <DialogContent dividers>
        {/* Basic Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Building Site */}
          <div>
            <label className="block font-semibold mb-1">Building Site</label>
            <input
              type="text"
              disabled={!isEditMode}
              className="w-full border border-gray-300 rounded-md p-2"
              value={localBuildingSite}
              onChange={(e) =>
                handleDeviceFieldChange('buildingSite', e.target.value)
              }
            />
            {validationErrors.buildingSite && (
              <p className="text-red-500 text-sm">{validationErrors.buildingSite}</p>
            )}
          </div>

          {/* Agency ID */}
          <div>
            <label className="block font-semibold mb-1">Agency ID</label>
            <input
              type="text"
              disabled={!isEditMode}
              className="w-full border border-gray-300 rounded-md p-2"
              value={localAgencyId}
              onChange={(e) => handleDeviceFieldChange('agencyId', e.target.value)}
            />
          </div>

          {/* Device ID */}
          <div>
            <label className="block font-semibold mb-1">Device ID</label>
            <input
              type="text"
              disabled={!isEditMode}
              className="w-full border border-gray-300 rounded-md p-2"
              value={deviceId}
              onChange={(e) => handleDeviceFieldChange('deviceId', e.target.value)}
            />
            {validationErrors.deviceId && (
              <p className="text-red-500 text-sm">{validationErrors.deviceId}</p>
            )}
          </div>

          {/* Device Type */}
          <div>
            <label className="block font-semibold mb-1">Device Type</label>
            <input
              type="text"
              disabled={!isEditMode}
              className="w-full border border-gray-300 rounded-md p-2"
              value={deviceType}
              onChange={(e) => handleDeviceFieldChange('deviceType', e.target.value)}
            />
          </div>

          {/* Device Sub Type */}
          <div>
            <label className="block font-semibold mb-1">Device Sub Type</label>
            <input
              type="text"
              disabled={!isEditMode}
              className="w-full border border-gray-300 rounded-md p-2"
              value={deviceSubType}
              onChange={(e) => handleDeviceFieldChange('deviceSubType', e.target.value)}
            />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={allowedToUpdateOther}
              onChange={(e) => setAllowedToUpdateOther(e.target.checked)}
            />
            <span>Allowed to update other devices</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={updateTestsAssigned}
              onChange={(e) => setUpdateTestsAssigned(e.target.checked)}
            />
            <span>Update tests assigned</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={updateDates}
              onChange={(e) => setUpdateDates(e.target.checked)}
            />
            <span>Update dates</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={updatePerformFields}
              onChange={(e) => setUpdatePerformFields(e.target.checked)}
            />
            <span>Update perform fields</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={updateDollarAmounts}
              onChange={(e) => setUpdateDollarAmounts(e.target.checked)}
            />
            <span>Update dollar amounts</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              disabled={!isEditMode}
              checked={updateWitnessFields}
              onChange={(e) => setUpdateWitnessFields(e.target.checked)}
            />
            <span>Update witness fields</span>
          </label>
        </div>

        {/* Sub-component: Device Inspections */}
        <DeviceInspections
          inspections={inspectionData}
          isEditMode={isEditMode}
          onInspectionsChange={setEditedInspections}
          editedInspections={editedInspections}
          buildingSite={localBuildingSite}
          agencyId={localAgencyId}
          deviceId={deviceId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ViewDeviceModal;
